import React, { useEffect, useRef } from "react";
import { useTranslation } from "next-i18next";
import { autocomplete } from "@algolia/autocomplete-js";
import { createQuerySuggestionsPlugin } from "@algolia/autocomplete-plugin-query-suggestions";
import { render as preactRender } from "preact";
import {
  getGlobalIndex,
  initAlgoliaSearch,
  getAlgoliaInsights,
  getInvestorIndex,
} from "../../lib/utils/AlgoliaSearch";
import { AlgoliaSearchHit } from "../../lib/types/algolia";
import { useAutocomplete } from "../../contexts/AutocompleteContext";

interface AutocompleteSearchProps {
  locale: string;
  section?: "main" | "investor";
  onResultClick: (hit: AlgoliaSearchHit) => void;
  onViewAllClick: (query: string) => void;
}

export const AutocompleteSearch: React.FC<AutocompleteSearchProps> = ({
  locale,
  section = "main",
  onResultClick,
  onViewAllClick,
}) => {
  const { t } = useTranslation("searchpage");
  const { t: tCommon } = useTranslation("common");
  const containerRef = useRef<HTMLDivElement>(null);
  const { autocompleteRef } = useAutocomplete();
  const searchClientRef = useRef(initAlgoliaSearch());
  const insightsRef = useRef(getAlgoliaInsights());
  const indexName =
    section === "investor" ? getInvestorIndex(locale) : getGlobalIndex(locale);
  const queryIDRef = useRef<string | undefined>(undefined);
  const totalResultsRef = useRef<number>(0);
  const currentQueryRef = useRef<string>("");

  useEffect(() => {
    if (!containerRef.current) {
      return;
    }

    const querySuggestionsPlugin = createQuerySuggestionsPlugin({
      searchClient: searchClientRef.current,
      indexName: `${indexName}_query_suggestions`,
      getSearchParams() {
        return { hitsPerPage: 5 };
      },
      transformSource({ source }) {
        return {
          ...source,
          onSelect({ item, setQuery, setIsOpen, refresh, event }) {
            const suggestion = item as { query: string };

            if (event) {
              event.preventDefault();
            }

            setQuery(suggestion.query);
            setIsOpen(true);
            refresh();
          },
          templates: {
            ...source.templates,
            header({ items, html }) {
              if (items.length === 0) return html``;
              return html`<span class="aa-SourceHeaderTitle"
                >${t("suggestions")}</span
              >`;
            },
            item({ item, html }) {
              const suggestion = item as { query: string };
              return html`<div
                class="aa-ItemWrapper aa-ItemWrapper--suggestion"
              >
                <div class="aa-ItemContent aa-ItemContent--suggestion">
                  <div class="aa-ItemIcon aa-ItemIcon--suggestion">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <path
                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                  </div>
                  <div class="aa-ItemContentBody">
                    <div
                      class="aa-ItemContentTitle aa-ItemContentTitle--suggestion"
                    >
                      ${suggestion.query}
                    </div>
                  </div>
                </div>
              </div>`;
            },
          },
        };
      },
    });

    const autocompleteInstance = autocomplete({
      container: containerRef.current,
      placeholder: t("input-label"),
      openOnFocus: true,
      detachedMediaQuery: "",
      initialState: {
        isOpen: false,
      },
      insights: true,
      translations: {
        detachedCancelButtonText: tCommon("close"),
      },
      id: "search-autocomplete",
      defaultActiveItemId: null,
      onSubmit({ event, setIsOpen }) {
        // Prevent form submission and keep modal open
        // This is a live search, so Enter key should do nothing
        event.preventDefault();
        setIsOpen(true);
      },
      render({ children }, root) {
        // Use Preact to render the autocomplete
        preactRender(children, root);

        // After rendering, attach event listeners to conditionally prevent Enter key
        setTimeout(() => {
          const formElement = root.querySelector(".aa-Form") as HTMLFormElement;
          const inputElement = root.querySelector(
            ".aa-Input"
          ) as HTMLInputElement;

          const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Enter") {
              // Only prevent Enter if no item is highlighted/selected
              // This allows arrow-navigated items to be selected with Enter
              // Check if any item has the aria-selected="true" attribute
              const hasActiveItem = root.querySelector(
                '[aria-selected="true"]'
              );
              if (!hasActiveItem) {
                // No item is selected, prevent default to keep modal open
                event.preventDefault();
                event.stopPropagation();
              }
              // If there IS an active item, let Algolia's handlers run normally
            }
          };

          if (formElement) {
            formElement.addEventListener("keydown", handleKeyDown, true);
            formElement.addEventListener(
              "submit",
              (e) => {
                e.preventDefault();
                e.stopPropagation();
              },
              true
            );
          }

          if (inputElement) {
            inputElement.addEventListener("keydown", handleKeyDown, true);
          }
        }, 0);
      },
      plugins: [querySuggestionsPlugin],
      getSources({ query }) {
        if (!query) {
          return [];
        }

        currentQueryRef.current = query;

        const sources = [
          {
            sourceId: "search-results",
            async getItems() {
              try {
                const results = await searchClientRef.current.search({
                  requests: [
                    {
                      indexName,
                      query,
                      hitsPerPage: 8,
                      attributesToRetrieve: [
                        "objectID",
                        "title",
                        "description",
                        "slug",
                      ],
                      attributesToSnippet: ["description:120"],
                      clickAnalytics: true,
                      analyticsTags: ["header-autocomplete"],
                    },
                  ],
                });

                const firstResult = results.results[0];
                if (firstResult && "hits" in firstResult) {
                  queryIDRef.current = firstResult.queryID;
                  totalResultsRef.current = firstResult.nbHits || 0;
                  return (firstResult.hits as AlgoliaSearchHit[]) || [];
                }
                queryIDRef.current = undefined;
                totalResultsRef.current = 0;
                return [];
              } catch (error) {
                //console.error("Search error:", error);
                queryIDRef.current = undefined;
                totalResultsRef.current = 0;
                return [];
              }
            },
            onSelect({ item }) {
              const hit = item as AlgoliaSearchHit;

              if (queryIDRef.current && hit.__position !== undefined) {
                try {
                  insightsRef.current("clickedObjectIDsAfterSearch", {
                    eventName: "Search Result Clicked",
                    index: indexName,
                    queryID: queryIDRef.current,
                    objectIDs: [hit.objectID],
                    positions: [hit.__position],
                  });
                } catch (error) {
                  //console.error("Error sending Algolia click event:", error);
                }
              }

              onResultClick(hit);
            },
            templates: {
              header({ items, html }) {
                if (items.length === 0) return html``;
                const count = items.length;
                const resultText =
                  count === 1 ? t("result_one") : t("result_other");
                return html`<span class="aa-SourceHeaderTitle"
                    >${t("results")}</span
                  >
                  <span class="aa-SourceHeaderCount"
                    >${count} ${resultText}</span
                  >`;
              },
              item({ item, html }) {
                const hit = item as AlgoliaSearchHit;
                const bodyAttr = hit.description?.trim()
                  ? "description"
                  : "content";
                const snippet = (hit[bodyAttr] as string) || "";
                const truncated =
                  snippet.length > 120
                    ? snippet.slice(0, 120) + "..."
                    : snippet;

                return html`<div
                  class="aa-ItemWrapper aa-ItemWrapper--withImage"
                >
                  <article class="aa-ItemContent aa-ItemContent--result">
                    <div class="aa-ItemContentBody">
                      <div class="aa-ItemContentTitle">${hit.title}</div>
                      ${truncated
                        ? html`<div class="aa-ItemContentDescription">
                            ${truncated}
                          </div>`
                        : null}
                    </div>
                  </article>
                </div>`;
              },
              noResults({ html }) {
                return html`<div class="aa-NoResults">
                  <div class="aa-NoResultsIcon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <path
                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                  </div>
                  <div class="aa-NoResultsTitle">${t("no-results-title")}</div>
                  <div class="aa-NoResultsMessage">
                    ${t("no-results-message")}
                  </div>
                </div>`;
              },
              footer({ state, html }) {
                if (state.status === "loading" || state.status === "stalled") {
                  return html`
                    <div
                      class="aa-LoadingIndicator"
                      role="status"
                      aria-live="polite"
                    >
                      <svg
                        class="aa-LoadingIcon"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <circle
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          stroke-width="2"
                          fill="none"
                          opacity="0.25"
                        />
                        <path
                          d="M12 2a10 10 0 0 1 10 10"
                          stroke="currentColor"
                          stroke-width="2"
                          fill="none"
                          stroke-linecap="round"
                        />
                      </svg>
                      <span class="aa-LoadingText">${t("searching")}</span>
                    </div>
                  `;
                }
                return html``;
              },
            },
          },
        ];

        if (totalResultsRef.current > 8) {
          sources.push({
            sourceId: "view-all-action",
            getItems() {
              return Promise.resolve([
                {
                  label: t("view-all-results"),
                  query: currentQueryRef.current,
                  // Add required AlgoliaSearchHit fields as empty/dummy values
                  objectID: "view-all-action",
                  title: t("view-all-results"),
                  slug: "",
                  content: "",
                } as AlgoliaSearchHit,
              ]);
            },
            onSelect() {
              onViewAllClick(currentQueryRef.current);
            },
            templates: {
              header() {
                return null;
              },
              item({ html }) {
                return html`<div class="aa-ItemWrapper">
                  <div class="aa-ItemContent aa-ItemContent--viewAll">
                    <div class="aa-ItemContentBody">
                      <div
                        class="aa-ItemContentTitle aa-ItemContentTitle--viewAll"
                      >
                        ${t("view-all-results")}
                      </div>
                    </div>
                  </div>
                </div>`;
              },
              noResults() {
                return null;
              },
              footer() {
                return null;
              },
            },
          });
        }

        return sources;
      },
    });

    autocompleteRef.current = autocompleteInstance;

    // MutationObserver as backup: Watch for modal DOM elements to be added
    // This ensures we catch the form even if the render function timing is off
    const observer = new MutationObserver(() => {
      const detachedContainer = document.querySelector(".aa-DetachedContainer");
      if (detachedContainer) {
        const formElement = detachedContainer.querySelector(
          ".aa-Form"
        ) as HTMLFormElement;
        const inputElement = detachedContainer.querySelector(
          ".aa-Input"
        ) as HTMLInputElement;

        const handleKeyDown = (event: KeyboardEvent) => {
          if (event.key === "Enter") {
            // Only prevent Enter if no item is highlighted/selected
            // Check if any item has the aria-selected="true" attribute
            const hasActiveItem = detachedContainer.querySelector(
              '[aria-selected="true"]'
            );
            if (!hasActiveItem) {
              // No item is selected, prevent default to keep modal open
              event.preventDefault();
              event.stopPropagation();
            }
            // If there IS an active item, let Algolia's handlers run normally
          }
        };

        if (formElement && !formElement.dataset.listenerAttached) {
          formElement.dataset.listenerAttached = "true";
          formElement.addEventListener("keydown", handleKeyDown, true);
          formElement.addEventListener(
            "submit",
            (e) => {
              e.preventDefault();
              e.stopPropagation();
            },
            true
          );
        }

        if (inputElement && !inputElement.dataset.listenerAttached) {
          inputElement.dataset.listenerAttached = "true";
          inputElement.addEventListener("keydown", handleKeyDown, true);
        }
      }
    });

    // Observe the entire document body for the detached modal appearing
    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return () => {
      observer.disconnect();
      autocompleteRef.current = null;
      autocompleteInstance.destroy();
    };
  }, [
    locale,
    indexName,
    t,
    tCommon,
    onResultClick,
    onViewAllClick,
    autocompleteRef,
  ]);

  return <div ref={containerRef} />;
};
